import { authStore } from './auth-store';
import { sunboundPayProxy } from '@proxies';

// localStorage is per-domain, so when redirecting between subdomains (e.g.
// login.sunboundhomes.com → sinceri.sunboundhomes.com, or when switching
// accounts across communities with different subdomains), the source domain
// issues a one-time opaque transfer token from the API and puts it in the
// redirect URL.  The functions below extract and redeem that token on arrival
// so that auth credentials land in localStorage on the target domain.
// (This is for Safari login support — Safari blocks cross-origin cookies.)

// Synchronously reads and strips the transferToken from the URL, returning it
// so the caller can redeem it asynchronously later.  Stripping happens
// immediately so the token is gone from the address bar before any other code
// (analytics, Sentry) runs.
export function extractTransferToken(): string | null {
    const params = new URLSearchParams(window.location.search);
    const transferToken = params.get(`transferToken`);
    if (!transferToken) return null;

    params.delete(`transferToken`);
    const newSearch = params.toString();
    window.history.replaceState({}, ``, window.location.pathname + (newSearch ? `?${newSearch}` : ``));

    return transferToken;
}

// Redeems a transfer token against the API and saves the resulting session to
// localStorage.  Only called when extractTransferToken returned a token.
export async function redeemTransferToken(transferToken: string): Promise<void> {
    try {
        const { token, tokenExpiresAt } = await sunboundPayProxy.redeemTransferToken(transferToken);
        authStore.setSession(token, tokenExpiresAt);
    } catch (error) {
        console.error(`Failed to redeem auth transfer token`, error);
    }
}

// Issues a transfer token and redirects to targetHost/targetPath with it as a
// query param.  Used when crossing subdomains (e.g. login → community subdomain,
// or account switch between communities).  localStorage is per-domain and Safari
// blocks cross-origin cookies, so the target domain redeems the token on arrival
// to obtain the real auth credentials.
export async function redirectWithAuthTransfer(targetHost: string, targetPath: string): Promise<void> {
    const { transferToken } = await sunboundPayProxy.issueTransferToken();
    const targetUrl = new URL(`${window.location.protocol}//${targetHost}${targetPath}`);
    targetUrl.searchParams.set(`transferToken`, transferToken);
    window.location.href = targetUrl.toString();
}
